import { compact } from "lodash";

import type { SessionSummary } from "../graph/types.ts";

const ACCESS_REQUEST_RESPONSE_PATTERN = /Request ID: (\w+)/;

const CHECK_RESPONSE_PATTERN =
  /Access (?:(granted)|was (denied)|is still in progress \(status: (\w+)\))/;

export type McpAccessLogEvent = {
  readonly id: string;
  readonly decodedToken?: {
    readonly email?: string;
    readonly azp?: string;
    readonly name?: string;
  };
  readonly event?: {
    readonly direction?: "inbound" | "outbound";
    readonly kind?: string;
    readonly sessionId?: string;
    readonly timestamp?: number;
    readonly transport?: string;
    readonly host?: string;
    readonly "mcp-server"?: {
      readonly id?: string;
      readonly name?: string;
      readonly path?: string;
    };
    readonly message?: {
      readonly id?: number | string;
      readonly kind?: "request" | "response";
      readonly method?: string;
      readonly params?: {
        readonly name?: string;
        readonly arguments?: Record<string, unknown>;
        readonly clientInfo?: {
          readonly name?: string;
          readonly title?: string;
          readonly version?: string;
        };
      };
      readonly result?: { readonly content?: { readonly text?: string }[] };
      readonly error?: unknown;
    };
  };
};

export type McpCallRecord = {
  readonly summary: SessionSummary;
  readonly sessionId: string;
  readonly request: McpAccessLogEvent;
  readonly response: McpAccessLogEvent;
};

const outcomeFor = (response: McpAccessLogEvent): SessionSummary["outcome"] => {
  const msg = response.event?.message;
  if (msg?.error !== undefined) return "denied";
  return "ok";
};

const groupKey = (e: McpAccessLogEvent): string | undefined => {
  const sessionId = e.event?.sessionId;
  const messageId = e.event?.message?.id;
  if (!sessionId || messageId === undefined) return undefined;
  return `${sessionId}:${messageId}`;
};

const summaryKindFor = (
  request: McpAccessLogEvent
): NonNullable<SessionSummary["kind"]> =>
  request.event?.message?.method === "tools/call" &&
  !!request.event.message.params?.name
    ? "tool-call"
    : "request";

/**
 * Pairs request + response access-log events by `${sessionId}:${messageId}`
 * and projects each pair into an `McpCallRecord`. Sorted descending by event
 * time and capped at `maxRecords`. Pure function — callers supply the
 * pre-filtered event list, which lets the same pairing logic serve both the
 * bulk-context hook and per-scope server-filtered fetches.
 */
export const pairAccessLogEvents = (
  events: readonly McpAccessLogEvent[],
  maxRecords: number
): McpCallRecord[] => {
  type Bucket = { request?: McpAccessLogEvent; response?: McpAccessLogEvent };
  const byKey = new Map<string, Bucket>();
  for (const e of events) {
    const key = groupKey(e);
    if (!key) continue;
    const bucket = byKey.get(key) ?? {};
    if (e.event?.message?.kind === "request") bucket.request = e;
    else if (e.event?.message?.kind === "response") bucket.response = e;
    byKey.set(key, bucket);
  }
  const out: McpCallRecord[] = [];
  for (const [key, { request, response }] of byKey) {
    if (!request || !response) continue;
    const kind = summaryKindFor(request);
    const { message, sessionId, timestamp } = request.event ?? {};
    const toolName =
      kind === "tool-call" ? message?.params?.name : message?.method;
    const label = toolName ?? message?.method ?? "tool";
    const startedMs = timestamp ?? 0;
    const endedMs = timestamp ?? startedMs;
    const responseContent = response?.event?.message?.result?.content;
    const requestId =
      kind === "tool-call" && toolName === "access"
        ? responseContent?.[0]?.text?.match(
            ACCESS_REQUEST_RESPONSE_PATTERN
          )?.[1]
        : (message?.params?.arguments?.request_id as string | undefined);
    const requestStatusMatch =
      kind === "tool-call" &&
      toolName === "check" &&
      responseContent?.[0]?.text?.match(CHECK_RESPONSE_PATTERN)?.slice(1, 4);
    const requestStatus = compact(
      requestStatusMatch || []
    )[0]?.toLocaleUpperCase();
    out.push({
      sessionId: sessionId ?? key,
      request,
      response,
      summary: {
        id: key,
        durationMs: Math.max(0, endedMs - startedMs),
        eventAt: new Date(startedMs).toISOString(),
        kind,
        outcome: outcomeFor(response),
        requestId,
        requestStatus: requestStatus === "GRANTED" ? "DONE" : requestStatus,
        toolName: label,
        userEmail: request.decodedToken?.email ?? "unknown",
      },
    });
  }
  out.sort(
    (a, b) =>
      new Date(b.summary.eventAt).getTime() -
      new Date(a.summary.eventAt).getTime()
  );
  return out.slice(0, maxRecords);
};
